import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useParams } from 'react-router-dom';

import { ArrowBack, ExpandMore, Link as LinkIcon, Refresh, Search } from '@mui/icons-material';
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Avatar,
	Box,
	Button,
	Chip,
	CircularProgress,
	Divider,
	FormControlLabel,
	InputAdornment,
	Link as MuiLink,
	Paper,
	Stack,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableRow,
	TextField,
	Typography,
} from '@mui/material';

import { ConversationClient, ConversationDetailViewModel, ConversationMessageContentViewModel, ConversationMessageViewModel } from '@@api';
import { formatTimestampFull, useApiClient } from '@@common';
import { AttributesTable, CopyToClipboardButton, CopyableText, DetailRow, DetailTable, ViewTitleBar } from '@@components';

import StyledMarkdown from '../../common/components/StyledMarkdown';
import ChatHtmlContent from '../../components/Chat/ChatHtmlContent';
import { getChatAttribute } from '../../components/Chat/ChatMessageAttributes';
import { ToolProgressGroup, buildChatDisplayItems, isToolProgressChatMessage } from '../../components/Chat/ChatProgress';
import ConversationAttachments from './ConversationAttachments';
import { prependConversationMessages, toConversationChatMessage } from './conversationMessageViewUtils';

function MessageContent({ content, allowHtml }: { content: ConversationMessageContentViewModel; allowHtml: boolean }) {
	const contentType = getChatAttribute(content.attributes, 'ContentType', 'sk:contentType')?.trim().toLowerCase();
	const isHtml = allowHtml && (contentType === 'html' || contentType === 'text/html' || contentType === 'sandboxed-html-v1');
	return (
		<Stack spacing={1} sx={{ minWidth: 0 }}>
			{content.text &&
				(isHtml ? (
					<ChatHtmlContent html={content.text} scripts={contentType === 'sandboxed-html-v1'} fileName={content.fileName} />
				) : (
					<StyledMarkdown
						sx={{
							overflowWrap: 'anywhere',
							lineHeight: 1.6,
						}}
					>
						{content.text}
					</StyledMarkdown>
				))}
			{content.linkUrl && (
				<Stack direction="row" spacing={1} alignItems="center">
					<LinkIcon fontSize="small" color="action" />
					<MuiLink href={content.linkUrl} target="_blank" rel="noreferrer">
						{content.linkName || content.linkUrl}
					</MuiLink>
				</Stack>
			)}
		</Stack>
	);
}

function ConversationMessage({ conversation, message }: { conversation: ConversationDetailViewModel; message: ConversationMessageViewModel }) {
	const participant = conversation.participants?.find((item) => item.id === message.participantId);
	const isUser = participant?.type.toLowerCase() === 'user';
	const attributeCount = message.contents.reduce((count, content) => count + Object.keys(content.attributes ?? {}).length, 0);

	return (
		<Paper
			variant="outlined"
			sx={{
				p: { xs: 1.5, sm: 2 },
				borderRadius: 2,
				maxWidth: isUser ? 840 : 1040,
				ml: isUser ? 'auto' : 0,
				bgcolor: isUser ? 'action.hover' : 'background.paper',
				borderLeftWidth: 3,
				borderLeftColor: isUser ? 'primary.main' : 'divider',
			}}
		>
			<Stack spacing={1.5} sx={{ minWidth: 0 }}>
				<Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} justifyContent="space-between">
					<Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
						<Avatar
							sx={{
								width: 26,
								height: 26,
								fontSize: '0.75rem',
								bgcolor: isUser ? 'primary.main' : 'action.selected',
								color: isUser ? 'primary.contrastText' : 'text.primary',
							}}
						>
							{participant?.name?.slice(0, 1).toUpperCase() ?? '?'}
						</Avatar>
						<Typography variant="subtitle2" noWrap>
							{participant?.name ?? 'Unknown participant'}
						</Typography>
						{participant?.type && <Chip size="small" label={participant.type} />}
					</Stack>
					<Stack direction="row" alignItems="center" spacing={1}>
						<Typography variant="caption" color="text.secondary">
							{formatTimestampFull(message.createdDate)}
						</Typography>
						<CopyToClipboardButton
							value={message.contents
								.map((content) => content.text)
								.filter(Boolean)
								.join('\n\n')}
							title="Copy message"
							edge={false}
						/>
					</Stack>
				</Stack>
				{(message.contents ?? []).map((content, index) => (
					<Stack key={content.id} spacing={1}>
						{index > 0 && <Divider />}
						<MessageContent content={content} allowHtml={!isUser} />
					</Stack>
				))}
				<ConversationAttachments contents={message.contents} />
				<Accordion disableGutters elevation={0} sx={{ bgcolor: 'transparent', '&:before': { display: 'none' } }}>
					<AccordionSummary expandIcon={<ExpandMore fontSize="small" />} sx={{ px: 0, minHeight: 28, '& .MuiAccordionSummary-content': { my: 0.5 } }}>
						<Typography variant="caption" color="text.secondary">
							Message details{attributeCount ? ` · ${attributeCount} attributes` : ''}
						</Typography>
					</AccordionSummary>
					<AccordionDetails sx={{ px: 0, py: 0.5 }}>
						<Stack spacing={1}>
							<DetailTable>
								<DetailRow label="Message ID" value={<CopyableText value={message.id} title="Copy message ID" />} />
								{message.groupId && <DetailRow label="Agent turn ID" value={<CopyableText value={message.groupId} title="Copy agent-turn group ID" />} />}
							</DetailTable>
							{message.contents
								.filter((content) => Object.keys(content.attributes ?? {}).length > 0)
								.map((content) => (
									<AttributesTable key={content.id} attributes={content.attributes} />
								))}
						</Stack>
					</AccordionDetails>
				</Accordion>
			</Stack>
		</Paper>
	);
}

function VirtualizedConversationMessages({
	conversation,
	messages,
	showRawMessages,
}: {
	conversation: ConversationDetailViewModel;
	messages: ConversationMessageViewModel[];
	showRawMessages: boolean;
}) {
	const parentRef = useRef<HTMLDivElement>(null);
	const messageById = useMemo(() => new Map(messages.map((message) => [message.id, message])), [messages]);
	const items = useMemo(() => {
		const chatMessages = messages.map((message) => toConversationChatMessage(message, conversation.participants));
		return showRawMessages ? chatMessages.map((message) => ({ kind: 'message' as const, message })) : buildChatDisplayItems(chatMessages);
	}, [conversation.participants, messages, showRawMessages]);
	const virtualizer = useVirtualizer({
		count: items.length,
		getScrollElement: () => parentRef.current,
		estimateSize: () => 240,
		overscan: 5,
		getItemKey: (index) => {
			const item = items[index];
			return item.kind === 'message' ? item.message.id : `progress:${item.messages[0].id}`;
		},
	});

	return (
		<Box ref={parentRef} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
			<Box sx={{ height: virtualizer.getTotalSize(), position: 'relative', width: '100%' }}>
				{virtualizer.getVirtualItems().map((virtualItem) => {
					const item = items[virtualItem.index];
					return (
						<Box
							key={virtualItem.key}
							data-index={virtualItem.index}
							ref={virtualizer.measureElement}
							sx={{ left: 0, position: 'absolute', top: 0, transform: `translateY(${virtualItem.start}px)`, width: '100%', pb: 1.5, pr: 1 }}
						>
							{item.kind === 'progressGroup' ? (
								<Stack spacing={1}>
									<ToolProgressGroup messages={item.messages} partialMessages={item.partialMessages} />
									<ConversationAttachments
										contents={[...item.messages, ...item.partialMessages].flatMap((message) => messageById.get(message.id)?.contents ?? [])}
									/>
								</Stack>
							) : (
								<ConversationMessage conversation={conversation} message={messageById.get(item.message.id)!} />
							)}
						</Box>
					);
				})}
			</Box>
		</Box>
	);
}

export default function ConversationDetailView() {
	const { id } = useParams<{ id: string }>();
	const location = useLocation();
	const client = useApiClient(ConversationClient);
	const [olderMessages, setOlderMessages] = useState<ConversationMessageViewModel[]>([]);
	const [hasMoreMessages, setHasMoreMessages] = useState(false);
	const [oldestMessageSequence, setOldestMessageSequence] = useState<number | undefined>();
	const [isLoadingOlderMessages, setIsLoadingOlderMessages] = useState(false);
	const [olderMessagesError, setOlderMessagesError] = useState<string | null>(null);
	const [searchText, setSearchText] = useState('');
	const [showRawMessages, setShowRawMessages] = useState(false);
	const [hideToolCalls, setHideToolCalls] = useState(false);

	const {
		data: conversation,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useQuery({
		queryKey: ['conversations', 'detail', id],
		queryFn: () => client.getById(id!),
		enabled: !!id,
	});

	useEffect(() => {
		setOlderMessages([]);
		setHasMoreMessages(conversation?.hasMoreMessages ?? false);
		setOldestMessageSequence(conversation?.oldestMessageSequence);
		setOlderMessagesError(null);
	}, [conversation?.hasMoreMessages, conversation?.id, conversation?.oldestMessageSequence]);

	const loadOlderMessages = useCallback(async () => {
		if (!id || oldestMessageSequence === undefined || isLoadingOlderMessages) {
			return;
		}

		setIsLoadingOlderMessages(true);
		setOlderMessagesError(null);
		try {
			const page = await client.getMessagesBefore(id, oldestMessageSequence, 50);
			setOlderMessages((messages) => [...page.messages, ...messages]);
			setHasMoreMessages(page.hasMoreMessages);
			setOldestMessageSequence(page.oldestMessageSequence);
		} catch (loadError) {
			setOlderMessagesError(loadError instanceof Error ? loadError.message : 'Failed to load older messages.');
		} finally {
			setIsLoadingOlderMessages(false);
		}
	}, [client, id, isLoadingOlderMessages, oldestMessageSequence]);

	const messages = useMemo(() => prependConversationMessages(olderMessages, conversation?.messages ?? []), [conversation?.messages, olderMessages]);
	const filteredMessages = useMemo(() => {
		const query = searchText.trim().toLowerCase();
		const visibleMessages = hideToolCalls ? messages.filter((message) => !isToolProgressChatMessage(toConversationChatMessage(message))) : messages;
		if (!query) return visibleMessages;
		return visibleMessages.filter(
			(message) =>
				message.contents.some((content) => `${content.text ?? ''} ${content.fileName ?? ''} ${content.linkName ?? ''}`.toLowerCase().includes(query)) ||
				conversation?.participants
					?.find((participant) => participant.id === message.participantId)
					?.name.toLowerCase()
					.includes(query),
		);
	}, [conversation?.participants, hideToolCalls, messages, searchText]);

	return (
		<Stack direction="column" spacing={2} sx={{ flexGrow: 1, flexShrink: 1, minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
			<ViewTitleBar title={conversation?.name ?? 'Conversation'}>
				<Button startIcon={<Refresh />} onClick={() => void refetch()} disabled={isFetching}>
					Refresh
				</Button>
				<Button component={RouterLink} to={{ pathname: '/conversation', search: location.search }} startIcon={<ArrowBack />}>
					Back
				</Button>
			</ViewTitleBar>

			{isLoading && (
				<Stack alignItems="center" sx={{ py: 6 }}>
					<CircularProgress />
				</Stack>
			)}

			{error && <Alert severity="error">{error instanceof Error ? error.message : 'Failed to load conversation.'}</Alert>}

			{conversation && (
				<Stack spacing={1.5} sx={{ px: 2, pb: 2, minWidth: 0, flex: 1, minHeight: 0, overflow: 'hidden' }}>
					<Stack direction="row" alignItems="center" gap={1} flexWrap="wrap" sx={{ flexShrink: 0 }}>
						<Chip size="small" label={`${messages.length} messages loaded`} />
						{conversation.participants?.map((participant) => (
							<Chip key={participant.id} size="small" variant="outlined" label={participant.name} />
						))}
						{conversation.updated && (
							<Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
								Updated {formatTimestampFull(conversation.updated)}
							</Typography>
						)}
					</Stack>
					<Accordion
						disableGutters
						elevation={0}
						sx={{
							flexShrink: 0,
							border: '1px solid',
							borderColor: 'divider',
							borderRadius: '8px !important',
							'&:before': { display: 'none' },
							'&.Mui-expanded': { my: 0 },
						}}
					>
						<AccordionSummary expandIcon={<ExpandMore />} sx={{ minHeight: 40, '& .MuiAccordionSummary-content': { my: 1 } }}>
							<Typography variant="body2" fontWeight={600}>
								Conversation details · Attributes · Participants
							</Typography>
						</AccordionSummary>
						<AccordionDetails sx={{ maxHeight: '35vh', overflow: 'auto' }}>
							<Stack spacing={2}>
								<Stack direction={{ xs: 'column', lg: 'row' }} spacing={2} alignItems="flex-start">
									<DetailTable>
										<DetailRow label="ID" value={<CopyableText value={conversation.id} title="Copy ID to clipboard" />} />
										<DetailRow label="Name" value={<CopyableText value={conversation.name} title="Copy name to clipboard" />} />
										<DetailRow label="Created" value={conversation.created ? formatTimestampFull(conversation.created) : ''} />
										<DetailRow label="Updated" value={conversation.updated ? formatTimestampFull(conversation.updated) : ''} />
										<DetailRow label="Expires On" value={conversation.expiresAt ? formatTimestampFull(conversation.expiresAt) : 'No expiration'} />
										<DetailRow label="Messages loaded" value={messages.length} />
									</DetailTable>
									<AttributesTable attributes={conversation.attributes} />
								</Stack>

								<TableContainer component={Paper} variant="outlined">
									<Table size="small">
										<TableBody>
											<TableRow>
												<TableCell colSpan={5}>
													<Typography variant="subtitle2">Participants</Typography>
												</TableCell>
											</TableRow>
											{(conversation.participants ?? []).length === 0 ? (
												<TableRow>
													<TableCell colSpan={5} sx={{ color: 'text.secondary' }}>
														No participants
													</TableCell>
												</TableRow>
											) : (
												(conversation.participants ?? []).map((participant) => (
													<TableRow key={participant.id}>
														<TableCell>{participant.name}</TableCell>
														<TableCell>{participant.type}</TableCell>
														<TableCell sx={{ overflowWrap: 'anywhere' }}>{participant.targetId}</TableCell>
														<TableCell>{participant.lastMessageStarted ? formatTimestampFull(participant.lastMessageStarted) : '-'}</TableCell>
														<TableCell align="right">
															<CopyToClipboardButton value={participant.targetId} title="Copy participant target ID" edge={false} />
														</TableCell>
													</TableRow>
												))
											)}
										</TableBody>
									</Table>
								</TableContainer>
							</Stack>
						</AccordionDetails>
					</Accordion>

					<Stack spacing={1.5} sx={{ flex: 1, minHeight: 0 }}>
						<Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} gap={1} sx={{ flexShrink: 0 }}>
							<TextField
								size="small"
								placeholder="Search loaded messages…"
								value={searchText}
								onChange={(event) => setSearchText(event.target.value)}
								slotProps={{
									htmlInput: { 'aria-label': 'Search loaded messages' },
									input: {
										startAdornment: (
											<InputAdornment position="start">
												<Search fontSize="small" />
											</InputAdornment>
										),
									},
								}}
								sx={{ flex: 1, maxWidth: 440 }}
							/>
							{searchText && (
								<Typography variant="caption" color="text.secondary">
									{filteredMessages.length} matches
								</Typography>
							)}
							<FormControlLabel
								sx={{ ml: { sm: 'auto' }, mr: 0 }}
								control={<Switch size="small" checked={hideToolCalls} onChange={(event) => setHideToolCalls(event.target.checked)} />}
								label={<Typography variant="body2">Hide tool calls</Typography>}
							/>
							<FormControlLabel
								sx={{ mr: 0 }}
								control={<Switch size="small" checked={showRawMessages} onChange={(event) => setShowRawMessages(event.target.checked)} />}
								label={<Typography variant="body2">Raw messages</Typography>}
							/>
						</Stack>
						{hasMoreMessages && (
							<Button variant="outlined" onClick={loadOlderMessages} disabled={isLoadingOlderMessages || oldestMessageSequence === undefined}>
								{isLoadingOlderMessages ? 'Loading older messages…' : 'Load older messages'}
							</Button>
						)}
						{olderMessagesError && <Alert severity="error">{olderMessagesError}</Alert>}
						{filteredMessages.length === 0 ? (
							<Paper variant="outlined" sx={{ p: 3, textAlign: 'center' }}>
								<Typography color="text.secondary">{searchText ? 'No loaded messages match your search.' : 'No messages'}</Typography>
							</Paper>
						) : (
							<VirtualizedConversationMessages
								conversation={conversation}
								messages={filteredMessages}
								showRawMessages={showRawMessages || Boolean(searchText.trim())}
							/>
						)}
					</Stack>
				</Stack>
			)}
		</Stack>
	);
}
